import { useMemo, useState } from 'react';
import { callApi } from '../lib/api.ts';
import {
  readPages,
  orderedPages,
  readSections,
  pageStatus,
  HOME_PAGE_ID,
  type Pages,
} from '../lib/sections.ts';
import type { Client } from '../lib/types.ts';
import { friendlyError } from '../lib/friendlyError.ts';

/**
 * The site's page manager (multi-page.md §2 M-P2) — pages as chips (Home always present) +
 * New page + per-secondary-page controls (rename · reorder · in-nav toggle · delete).
 * Server-authoritative CRUD via build-chat-pages (read-modify-write on wizard_state.pages);
 * a tree change regenerates the shared Nav/Footer chrome server-side.
 *
 * IA-2: extracted from BuildChat so it can be surfaced both as its OWN wizard step
 * ("Site structure", BEFORE SEO/Build) and INSIDE Build — one component, no drift. It's
 * CONTROLLED on the active page: the parent owns the active-page UI state (so Build's
 * section rail stays in lock-step); this component persists the view-pointer ('activate')
 * and notifies the parent via onSwitchPage.
 */
export function PageManager({
  client,
  activePage,
  onSwitchPage,
  onActivity,
  disabled = false,
}: {
  client: Client;
  /** The parent's active page id (drives Build's rail; the standalone step keeps its own). */
  activePage: string;
  /** Notify the parent the active page changed — the parent updates its local UI state. */
  onSwitchPage: (pageId: string) => void;
  /** Refresh the client's wizard_state after a mutation (pages/activePageId changed). */
  onActivity: () => void;
  /** Live/protected → the page manager is read-only (create/edit/delete hidden). */
  disabled?: boolean;
}) {
  const pages = useMemo<Pages>(() => readPages(client.wizard_state), [client.wizard_state]);
  const hasPageLayer = Object.keys(pages).length > 0;
  // Always show at least a Home chip: real pages, else a synthetic Home over the flat/legacy
  // sections (a pre-M-P2 client) so the manager reads consistently before any page exists.
  const pageList = useMemo(() => {
    const ordered = orderedPages(pages);
    if (ordered.length) return ordered;
    return [{ id: HOME_PAGE_ID, slug: 'home', name: 'Home', order: 0, inNav: true, isHome: true, sections: readSections(client.wizard_state, HOME_PAGE_ID) }];
  }, [pages, client.wizard_state]);
  const effectivePage = hasPageLayer && pages[activePage] ? activePage : HOME_PAGE_ID;
  const isHomePage = !hasPageLayer || effectivePage === HOME_PAGE_ID || pages[effectivePage]?.isHome === true;

  const [pageBusy, setPageBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function pageAction(action: string, extra: Record<string, unknown>): Promise<{ pageId?: string } | null> {
    setErr(null);
    setPageBusy(true);
    try {
      const res = await callApi<{ pageId?: string }>('build-chat-pages', {
        method: 'POST',
        body: JSON.stringify({ clientId: client.id, action, ...extra }),
      });
      onActivity(); // refresh wizard_state (pages / activePageId changed)
      return res;
    } catch (e) {
      setErr(friendlyError(e, (e as Error).message));
      return null;
    } finally {
      setPageBusy(false);
    }
  }

  // Switch the active page: tell the parent (instant UI), persist the view-pointer
  // best-effort (a failed persist just means it won't survive a reload — the switch is
  // still visible this session).
  function switchPage(pageId: string) {
    onSwitchPage(pageId);
    void callApi('build-chat-pages', {
      method: 'POST',
      body: JSON.stringify({ clientId: client.id, action: 'activate', pageId }),
    }).catch(() => {});
  }
  async function addPage() {
    const name = window.prompt('New page name (e.g. About, Contact):')?.trim();
    if (!name) return;
    const res = await pageAction('create', { name });
    if (res?.pageId) onSwitchPage(res.pageId); // server already activated it — follow the designer in
  }
  async function renamePagePrompt(pageId: string, current: string) {
    const name = window.prompt('Rename page:', current)?.trim();
    if (!name || name === current) return;
    await pageAction('rename', { pageId, name });
  }
  async function deletePage(pageId: string, name: string) {
    if (!window.confirm(`Delete the "${name}" page? Its committed route/components (if any) stay on the branch — ask the chat to remove them.`)) return;
    if (effectivePage === pageId) onSwitchPage(HOME_PAGE_ID);
    await pageAction('delete', { pageId });
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-ink-2">Pages</span>
        {pageList.map((p) => {
          const isActive = p.id === effectivePage;
          const st = pageStatus(p);
          return (
            <button
              key={p.id}
              onClick={() => switchPage(p.id)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${
                isActive ? 'border-ink bg-ink text-paper' : 'border-line bg-paper text-ink-2 hover:bg-paper-2'
              }`}
              title={p.isHome ? 'Home (the root page)' : `/${p.slug}`}
            >
              {p.isHome && <span aria-hidden>🏠</span>}
              <span>{p.name}</span>
              {st === 'locked' ? (
                <span title="all sections locked" className={isActive ? '' : 'text-success'}>✓</span>
              ) : st === 'draft' ? (
                <span className="opacity-60" title="in progress">•</span>
              ) : null}
              {!p.inNav && <span className="opacity-60" title="hidden from nav">⊘</span>}
            </button>
          );
        })}
        {!disabled && (
          <button
            onClick={() => void addPage()}
            disabled={pageBusy}
            className="rounded-full border border-line border-dashed px-3 py-1 text-xs text-ink-2 hover:bg-paper-2 disabled:opacity-50"
          >
            ＋ New page
          </button>
        )}
      </div>
      {/* Controls for the active SECONDARY page (Home is pinned — no rename/move/delete). */}
      {!disabled && hasPageLayer && !isHomePage && pages[effectivePage] && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-line bg-paper-2 px-2 py-1.5 text-xs text-ink-2">
          <button onClick={() => void renamePagePrompt(effectivePage, pages[effectivePage].name)} disabled={pageBusy} className="rounded border border-line bg-paper px-2 py-0.5 hover:bg-paper-2 disabled:opacity-50">Rename</button>
          <button onClick={() => void pageAction('move', { pageId: effectivePage, direction: 'up' })} disabled={pageBusy} title="Move earlier in the nav" className="rounded border border-line bg-paper px-2 py-0.5 hover:bg-paper-2 disabled:opacity-50">↑</button>
          <button onClick={() => void pageAction('move', { pageId: effectivePage, direction: 'down' })} disabled={pageBusy} title="Move later in the nav" className="rounded border border-line bg-paper px-2 py-0.5 hover:bg-paper-2 disabled:opacity-50">↓</button>
          <label className="inline-flex items-center gap-1">
            <input
              type="checkbox"
              checked={pages[effectivePage].inNav}
              onChange={(e) => void pageAction('nav', { pageId: effectivePage, inNav: e.target.checked })}
              disabled={pageBusy}
            />
            In nav
          </label>
          <button onClick={() => void deletePage(effectivePage, pages[effectivePage].name)} disabled={pageBusy} className="ml-auto rounded px-1.5 py-0.5 text-danger hover:bg-danger-bright/10 disabled:opacity-50">Delete page</button>
        </div>
      )}
      {err && <p className="text-xs text-danger">{err}</p>}
    </div>
  );
}
